import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import UserAvatar from '../ui/UserAvatar';
import './Layout.css';

import HomeIcon from '@mui/icons-material/HomeRounded';
import CheckroomIcon from '@mui/icons-material/CheckroomRounded';
import ChatIcon from '@mui/icons-material/ChatBubbleRounded';
import StyleIcon from '@mui/icons-material/AutoAwesomeRounded';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import AddIcon from '@mui/icons-material/AddRounded';
import MenuIcon from '@mui/icons-material/MenuRounded';
import CloseIcon from '@mui/icons-material/CloseRounded';
import SocialIcon from '@mui/icons-material/Diversity3Rounded';
import HistoryIcon from '@mui/icons-material/HistoryRounded';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutwardRounded';
import CameraAltIcon from '@mui/icons-material/CameraAltRounded';

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const contentScrollRef = useRef(null);

  useEffect(() => {
    contentScrollRef.current?.scrollTo({ top: 0, behavior: 'auto' });
  }, [location.pathname]);

  const navItems = [
    {
      name: 'Dashboard',
      subtitle: 'Overview and quick actions',
      path: '/',
      icon: <HomeIcon />,
    },
    {
      name: 'Closet',
      subtitle: 'Manage your wardrobe',
      path: '/closet',
      icon: <CheckroomIcon />,
    },
    {
      name: 'Social Feed',
      subtitle: 'Discover community looks',
      path: '/social',
      icon: <SocialIcon />,
    },
    {
      name: 'AI Stylist',
      subtitle: 'Get instant recommendations',
      path: '/chat',
      icon: <ChatIcon />,
    },
    {
      name: 'Style Profile',
      subtitle: 'Tune your style preferences',
      path: '/style-profile',
      icon: <StyleIcon />,
    },
    {
      name: 'Outfit History',
      subtitle: 'Track what you wore',
      path: '/outfit-history',
      icon: <HistoryIcon />,
    },
  ];

  const isRouteActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const currentNav = navItems.find((item) => isRouteActive(item.path));
  const userName = user?.name || user?.username || 'FashionMate User';
  const firstName = userName.split(' ')[0];

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="app-shell">
      <aside className={`app-sidebar glass-panel ${mobileMenuOpen ? 'mobile-open' : ''}`}>
        <div className="sidebar-brand-row">
          <button className="brand" onClick={() => navigate('/')}>
            <span className="brand-mark">FM</span>
            <div className="brand-copy">
              <span className="brand-name">FashionMate</span>
              <span className="brand-meta">AI style workspace</span>
            </div>
          </button>
          <button className="mobile-close-btn" onClick={() => setMobileMenuOpen(false)} aria-label="Close menu">
            <CloseIcon />
          </button>
        </div>

        <button className="sidebar-profile-card" onClick={() => navigate(`/profile/${user?.id}`)}>
          <UserAvatar user={user} size={42} showBorder />
          <div className="sidebar-profile-copy">
            <span className="profile-greeting">Welcome back</span>
            <span className="profile-name">{firstName}</span>
          </div>
          <ArrowOutwardIcon fontSize="small" />
        </button>

        <nav className="sidebar-nav" aria-label="Primary">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`nav-item ${isRouteActive(item.path) ? 'active' : ''}`}
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="nav-icon">{item.icon}</span>
              <span className="nav-copy">
                <span className="nav-label">{item.name}</span>
                <span className="nav-subtitle">{item.subtitle}</span>
              </span>
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Link to="/closet/scan" className="sidebar-action btn-scan" onClick={() => setMobileMenuOpen(false)}>
            <CameraAltIcon fontSize="small" />
            Scan My Closet
          </Link>
          <Link to="/closet/add" className="sidebar-action btn-primary" onClick={() => setMobileMenuOpen(false)}>
            <AddIcon fontSize="small" />
            Add New Item
          </Link>
          <button className="sidebar-logout" onClick={handleLogout} title="Logout">
            <LogoutIcon fontSize="small" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="app-main">
        <header className="app-topbar glass-panel-heavy">
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)} aria-label="Open menu">
            <MenuIcon />
          </button>

          <div className="topbar-title-wrap">
            <p className="topbar-kicker">{currentNav?.subtitle || 'Manage your workspace'}</p>
            <h1 className="topbar-title">{currentNav?.name || 'FashionMate'}</h1>
          </div>

          <div className="topbar-actions">
            <Link to="/closet/add" className="topbar-primary-action">
              <AddIcon fontSize="small" />
              <span>Add Item</span>
            </Link>
            <button className="topbar-user-pill" onClick={() => navigate(`/profile/${user?.id}`)}>
              <UserAvatar user={user} size={30} />
              <span>{firstName}</span>
            </button>
          </div>
        </header>

        <div className="app-content-scroll" ref={contentScrollRef}>
          <div className="content-wrapper animate-fade-in">{children}</div>
        </div>
      </main>

      {mobileMenuOpen && <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />}
    </div>
  );
};

export default Layout;
